import React, { createContext, useState, useContext, useCallback } from "react";
import {
  handleImageUpdateWithScroll,
  handleVideoUpdateWithScroll,
  sendScrollMessage,
  MESSAGE_TYPES,
} from "../utils/editIframe";

const MediaContext = createContext();

export function MediaProvider({ children }) {
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);
  const [targetField, setTargetField] = useState(null); // どのフィールド用に選択しているか
  const [fieldSetters, setFieldSetters] = useState({}); // フィールド名とセッター関数のマッピング
  const [currentMediaType, setCurrentMediaType] = useState(null); // 'image' または 'video'

  // 複数選択モード用の追加ステート
  const [selectionMode, setSelectionMode] = useState("single");
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([]);
  const [isMultipleFieldSetter, setIsMultipleFieldSetter] = useState({});

  // リサイズ横幅の状態管理
  const [savedResizeWidth, setSavedResizeWidth] = useState(null);

  // スクロール位置の状態管理
  const [savedScrollPosition, setSavedScrollPosition] = useState(null);

  // メディアを選択（複数選択対応）
  const selectMedia = useCallback(
    (media) => {
      if (selectionMode === "multiple") {
        setSelectedMediaFiles((prev) => {
          // 既に選択されているかチェック
          const isAlreadySelected = prev.some((item) => item.id === media.id);

          if (isAlreadySelected) {
            // 選択解除
            return prev.filter((item) => item.id !== media.id);
          } else {
            // 選択追加
            return [...prev, media];
          }
        });
      } else {
        // 単一選択モード
        setSelectedMediaFile(media);
      }
    },
    [selectionMode]
  );

  // リサイズ横幅を保存
  const saveResizeWidth = useCallback((width) => {
    setSavedResizeWidth(width);
  }, []);

  // リサイズ横幅を復元
  const restoreResizeWidth = useCallback(() => {
    return savedResizeWidth;
  }, [savedResizeWidth]);

  // スクロール位置を保存
  const saveScrollPosition = useCallback(() => {
    const editArea = document.querySelector(".editarea");
    if (editArea) {
      setSavedScrollPosition(editArea.scrollTop);
    }
  }, []);

  // スクロール位置を復元
  const restoreScrollPosition = useCallback(() => {
    if (savedScrollPosition !== null) {
      const editArea = document.querySelector(".editarea");
      if (editArea) {
        // 少し遅延してからスクロール位置を復元
        setTimeout(() => {
          editArea.scrollTop = savedScrollPosition;
        }, 0);
      }
      setSavedScrollPosition(null); // 復元後はクリア
    }
  }, [savedScrollPosition]);

  // メディアを閉じる
  const closeMedia = useCallback(() => {
    setIsMediaOpen(false);
    setTargetField(null);
    setSelectedMediaFiles([]);
    // スクロール位置を復元
    restoreScrollPosition();
  }, [restoreScrollPosition]);

  // セッター関数を登録（複数選択対応）
  const registerSetter = useCallback(
    (fieldName, setterFunction, isMultiple = false) => {
      // fieldSettersとisMultipleFieldSetterを同時に更新
      setFieldSetters((prev) => ({
        ...prev,
        [fieldName]: setterFunction,
      }));

      setIsMultipleFieldSetter((prev) => ({
        ...prev,
        [fieldName]: isMultiple,
      }));
    },
    []
  );

  // メディアを開く（複数選択対応）
  const openMedia = useCallback(
    (fieldName, currentMedia = null, mediaType = null, mode = "single") => {
      setIsMediaOpen(true);
      setTargetField(fieldName);
      setCurrentMediaType(mediaType);
      setSelectionMode(mode);

      // 現在のメディアが渡された場合、それを選択状態にする
      if (mode === "multiple") {
        setSelectedMediaFiles(Array.isArray(currentMedia) ? currentMedia : []);
        setSelectedMediaFile(null);
      } else {
        setSelectedMediaFile(currentMedia);
        setSelectedMediaFiles([]);
      }

      // スクロール位置を保存してから一番上まで戻す
      saveScrollPosition();
      const editArea = document.querySelector(".editarea");
      if (editArea) {
        editArea.scrollTop = 0;
      }

      // URLからページを判断
      const currentPath = window?.location?.pathname || "";
      let pageContentKey = "topContent";
      if (currentPath.includes("about")) {
        pageContentKey = "aboutContent";
      } else if (currentPath.includes("studio")) {
        pageContentKey = "studioContent";
      } else if (currentPath.includes("instructor")) {
        pageContentKey = "instructorContent";
      } else if (currentPath.includes("price")) {
        pageContentKey = "priceContent";
      } else if (currentPath.includes("newvisitor")) {
        pageContentKey = "newVisitorContent";
      } else if (currentPath.includes("access")) {
        pageContentKey = "accessContent";
      }

      // targetFieldに基づいてcomponentKeyを決定
      let componentKey = null;

      // iframeにスクロール命令を送信
      sendScrollMessage(fieldName, null, {
        contentKey: pageContentKey,
        componentKey,
      });
    },
    [saveScrollPosition]
  );

  // メディアファイルを取得
  const fetchMediaFiles = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_media_files/`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setMediaFiles(data);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
  }, []);

  // 選択したメディアを確定（複数選択対応）
  const confirmMediaSelection = useCallback(() => {
    if (!targetField || !fieldSetters[targetField]) {
      console.warn("No field setter found for", targetField);
      return;
    }

    const isMultiple = isMultipleFieldSetter[targetField];

    if (isMultiple) {
      // 複数選択モードの場合
      if (selectedMediaFiles.length === 0) return;
      fieldSetters[targetField](selectedMediaFiles);

      // ページに応じたcontentKeyを決定
      let pageContentKey = "topContent";
      const currentPath = window?.location?.pathname || "";
      if (currentPath.includes("about")) {
        pageContentKey = "aboutContent";
      } else if (currentPath.includes("studio")) {
        pageContentKey = "studioContent";
      } else if (currentPath.includes("instructor")) {
        pageContentKey = "instructorContent";
      } else if (currentPath.includes("price")) {
        pageContentKey = "priceContent";
      } else if (currentPath.includes("newvisitor")) {
        pageContentKey = "newVisitorContent";
      } else if (currentPath.includes("access")) {
        pageContentKey = "accessContent";
      }

      // targetFieldに基づいてcomponentKeyを決定
      let componentKey = null;

      // iframeに通知
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
            target: `.edit_${targetField}`,
            contentKey: pageContentKey,
            componentKey,
            fieldName: targetField,
            value: selectedMediaFiles,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    } else {
      // 単一選択モードの場合
      if (!selectedMediaFile) return;
      fieldSetters[targetField](selectedMediaFile);

      // ページに応じたcontentKeyを決定
      let pageContentKey = "topContent";
      // URLからページを判断
      const currentPath = window.location.pathname;
      if (currentPath.includes("about")) {
        pageContentKey = "aboutContent";
      } else if (currentPath.includes("studio")) {
        pageContentKey = "studioContent";
      } else if (currentPath.includes("instructor")) {
        pageContentKey = "instructorContent"; // instructorページを追加
      } else if (currentPath.includes("price")) {
        pageContentKey = "priceContent"; // priceページを追加
      } else if (currentPath.includes("newvisitor")) {
        pageContentKey = "newVisitorContent";
      } else if (currentPath.includes("access")) {
        pageContentKey = "accessContent";
      } else if (currentPath.includes("news")) {
        pageContentKey = "newsContent"; // newsページを追加
      }

      // targetFieldに基づいてcomponentKeyを決定
      let componentKey = null;
      if (
        targetField === "hero_hedder_image" ||
        targetField.includes("pc_video_") ||
        targetField.includes("mobile_video_")
      ) {
        componentKey = currentPath.includes("about") ? "about" : "heroHeader";
      } else if (targetField === "about_image") {
        componentKey = "story";
      } else if (targetField === "newvisitor_bg_image") {
        componentKey = "newvisitor";
      } else if (targetField.includes("concept_description_")) {
        componentKey = "about";
      } else if (targetField.includes("main_image")) {
        componentKey = "studio";
      } else if (targetField.includes("instructor_")) {
        componentKey = "instructor"; // instructorフィールドを追加
      } else if (targetField === "page_image") {
        componentKey = "price"; // priceページのpage_imageフィールドを追加
      } else if (targetField === "image") {
        componentKey = "news"; // newsページのimageフィールドを追加
      }

      let isVideo = false;
      // 動画フィールドの場合
      if (targetField.includes("video")) {
        isVideo = true;
      }

      // 画像または動画の更新メッセージを送信（スクロール付き）
      if (isVideo) {
        handleVideoUpdateWithScroll(selectedMediaFile, targetField, {
          contentKey: pageContentKey,
          componentKey,
        });
      } else {
        handleImageUpdateWithScroll(selectedMediaFile, targetField, {
          contentKey: pageContentKey,
          componentKey,
        });
      }
    }

    closeMedia();
  }, [
    selectedMediaFile,
    selectedMediaFiles,
    targetField,
    fieldSetters,
    isMultipleFieldSetter,
    closeMedia,
  ]);

  // 単一メディアの削除チェック（参照チェックのみ）
  const checkMediaReference = useCallback(
    async (media) => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/delete_media/${media.id}/`,
          {
            method: "DELETE",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        return { success: response.ok, data, media };
      } catch (error) {
        return { success: false, error: error.message, media };
      }
    },
    []
  );

  // 複数メディアの削除（バッチ処理）
  const handleDeleteMediaBatch = useCallback(
    async (mediaList) => {
      if (!mediaList || mediaList.length === 0) return;

      // 参照チェックを並列実行
      const checkPromises = mediaList.map(media => checkMediaReference(media));
      const results = await Promise.all(checkPromises);

      // 削除できない画像と削除可能な画像を分ける
      const cannotDelete = [];
      const canDelete = [];

      results.forEach(result => {
        if (!result.success && result.data?.references) {
          cannotDelete.push({
            media: result.media,
            references: result.data.references
          });
        } else if (result.success) {
          canDelete.push(result.media);
        }
      });

      // 削除できない画像がある場合はアラートを表示
      if (cannotDelete.length > 0) {
        const referenceTexts = cannotDelete.map(item => {
          const mediaName = item.media.image_url.substring(item.media.image_url.lastIndexOf('/') + 1);
          const referenceText = item.references
            .map(ref => `${ref.model_display}の${ref.field_display}で${ref.count}件使用中`)
            .join(", ");
          return `• ${mediaName}: ${referenceText}`;
        });

        alert(
          `以下の画像は削除できません：\n\n${referenceTexts.join("\n")}\n\n削除可能な画像のみ削除されます。`
        );
      }

      // 削除可能な画像があれば削除処理を実行
      if (canDelete.length > 0) {
        // メディアリストを更新
        await fetchMediaFiles();

        // 選択状態をクリア
        setSelectedMediaFile(null);
        setSelectedMediaFiles([]);
      }
    },
    [checkMediaReference, fetchMediaFiles]
  );

  // 単一メディア削除（互換性のため）
  const handleDeleteMedia = useCallback(
    async (media) => {
      await handleDeleteMediaBatch([media]);
    },
    [handleDeleteMediaBatch]
  );

  // ByteをMBに変換
  const bytesToMB = useCallback((bytesSize) => {
    if (bytesSize === null || bytesSize === undefined) {
      return null;
    }
    const mbSize = bytesSize / (1024 * 1024);
    return mbSize.toFixed(2); // 小数点以下2桁に丸める
  }, []);

  return (
    <MediaContext.Provider
      value={{
        isMediaOpen,
        mediaFiles,
        selectedMediaFile,
        selectedMediaFiles,
        selectionMode,
        targetField,
        currentMediaType,
        openMedia,
        closeMedia,
        selectMedia,
        confirmMediaSelection,
        registerSetter,
        handleDeleteMedia,
        handleDeleteMediaBatch,
        bytesToMB,
        fetchMediaFiles,
        setMediaFiles,
        saveResizeWidth,
        restoreResizeWidth,
        saveScrollPosition,
        restoreScrollPosition,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
}

export function useMedia() {
  return useContext(MediaContext);
}
